<template>
  <main class="value">
    <Breadcrumb></Breadcrumb>
    <PageHeader
      title="Equipping students for the real world"
      backgroundImage="value/value-hero3.jpg"
      backgroundColor="transparent"
      textColor="white"
      overlay="dark"
      align="center"
      style="background-position: bottom"
      size="lg"
    >
      <p>
        The University of Georgia’s innovative courses, study abroad
        opportunities, and student-centered initiatives bring exceptional value
        to public higher education. UGA graduates are prepared for their
        academic and professional lives beyond the campus.
      </p>
    </PageHeader>
    <section class="row g-0">
      <div class="col-md-4 border-right">
        <Figure
          :endAmount="190"
          :startAmount="160"
          suffix="+"
          subheading="UGA study abroad programs (2021&#8209;22)"
          backgroundColor="red"
          textColor="white"
          textured
        ></Figure>
      </div>
      <div class="col-md-4 border-right">
        <Figure
          :endAmount="96"
          :startAmount="80"
          suffix="%"
          subheading="of 2022 graduates were employed or attending graduate school within 6 months"
          backgroundColor="black"
          textColor="white"
          textured
        ></Figure>
      </div>
      <div class="col-md-4">
        <Figure
          heading="Courses offered in"
          :endAmount="88"
          :startAmount="50"
          subheading="countries and across all continents (2021&#8209;22)"
          backgroundColor="grey"
          textColor="white"
          textured
        ></Figure>
      </div>
    </section>
    <section style="margin: 5rem 0">
      <div class="row container-lg g-5 mx-auto">
        <div class="col-md-6" data-aos="fade-up">
          <QuickFact
            imageURL="value/experiential-learning.jpg"
            heading="A National Leader in Experiential Learning"
          >
            <p>
              The University of Georgia is one of the nation’s largest
              universities to offer
              <b>100% of undergraduate students hands-on learning experiences</b
              >, including internships, research opportunities, and study
              abroad. Experiential learning enhances student success both in and
              beyond the classroom.
            </p>
            <Btn
              color="black"
              size="sm"
              outline
              href="https://uga.campuslabs.com/engage/"
              >Explore More Opportunities</Btn
            >
          </QuickFact>
        </div>
        <div class="col-md-6" data-aos="fade-up">
          <QuickFact
            imageURL="value/mentors.jpg"
            heading="Relationships with Professionals"
          >
            <p>
              We provide one-on-one mentorships that connect students with
              successful professionals in our alumni network. This personal
              attention gives our students an edge in pursuing their chosen
              fields. In 2022, the UGA Mentor Program received the CASE Gold
              Circle of Excellence Award for Student Engagement.
            </p>
            <Btn color="black" size="sm" outline href="https://mentor.uga.edu/"
              ><span class="visually-hidden">Learn about the</span>UGA Mentor
              Program</Btn
            >
          </QuickFact>
        </div>
      </div>
    </section>
    <Section data-aos="fade" type="full" imgSide="right" imgURL="img1.png">
      <h3>Double Dawgs</h3>
      <p>
        The Double Dawgs program allows ambitious and motivated students to earn
        both a bachelor’s degree and a master’s degree in five years or less.
        Students choose from more than 100 different degree combinations across
        various departments and colleges, gaining a distinct competitive
        advantage in today’s economy.
      </p>
      <Btn color="black" size="lg" outline href="https://doubledawgs.uga.edu/"
        >Learn More</Btn
      >
    </Section>
    <section style="margin: 5rem 0">
      <div class="row container-lg g-5 mx-auto">
        <div class="col-md-6" data-aos="fade-up">
          <QuickFact
            imageURL="value/36810-025.jpg"
            heading="Student Life at UGA"
          >
            <p>
              University of Georgia students commit to a complete and engaging
              college experience. With more than 800 unique organizations, our
              students connect with peers and mentors who share their interests
              and career goals.
            </p>
            <Btn
              color="black"
              size="sm"
              outline
              href="https://uga.campuslabs.com/engage/"
              >Explore Student Organizations</Btn
            >
          </QuickFact>
        </div>
        <div class="col-md-6" data-aos="fade-up">
          <QuickFact
            imageURL="value/37096-023.jpg"
            heading="Small Class Initiative"
          >
            <p>
              The University of Georgia’s Small Class Initiative allowed us to
              expand accessibility to high-demand classes and enhance student
              outcomes by reducing class sizes. We invested $4.4 million to hire
              56 faculty members and create 319 new course sections in 81
              majors.
            </p>
            <Btn
              color="black"
              size="sm"
              outline
              href="https://ovpi.uga.edu/initiatives/small-class-sizes/"
              ><span class="visually-hidden">Learn about the</span>Small Class
              Initiative</Btn
            >
          </QuickFact>
        </div>
      </div>
    </section>
    <section>
      <Section
        type="standard"
        imgSide="right"
        imgURL="value/committed-to-diversity.jpg"
        color="bg-red bg-diagonal--dark"
        class="text-white"
      >
        <h3>Committed to Diversity</h3>
        <p>
          For the eighth year in a row, the University of Georgia was recognized
          for our commitment to diversity and inclusion through a national
          Higher Education Excellence in Diversity Award. Our Diversity and
          Inclusive Excellence Plan, developed in 2021, is focused on building
          an inclusive environment, workforce, and community.
        </p>
        <p>
          We continuously develop new and innovative initiatives to encourage a
          diverse and inclusive culture, including:
        </p>
        <ul>
          <li>
            The
            <a
              class="text-white text-decoration-underline"
              href="https://dae.uga.edu/initiatives/all-georgia-program/"
              >ALL Georgia Program</a
            >, which focuses on students from rural parts of the state.
          </li>
          <li>
            The
            <a
              class="text-white text-decoration-underline"
              href="https://diversity.uga.edu/partnerships/gaame_scholars_program/"
              >Georgia African American Male Experience</a
            >, which provides support and mentoring.
          </li>
          <li>
            The
            <a
              class="text-white text-decoration-underline"
              href="https://seercenter.uga.edu/research/current-projects/delta-project/"
              >DeLTA project</a
            >, which promotes diversity in STEM fields.
          </li>
        </ul>
        <Btn color="white" size="lg" href="https://diversity.uga.edu/" outline
          >Office of Institutional Diversity</Btn
        >
      </Section>
    </section>
  </main>
</template>

<script>
// @ is an alias to /src
import Breadcrumb from "@/components/Breadcrumb.vue";
import PageHeader from "@/components/PageHeader.vue";
import Figure from "@/components/Figure.vue";
import QuickFact from "@/components/QuickFact.vue";
import Btn from "@/components/Btn.vue";
import Section from "@/components/Section.vue";

export default {
  name: "Value",
  metaInfo: {
    title: "Top Value at UGA",
    description:
      "Our innovative courses, programs, and student-centered initiatives are preparing students for their academic and professional lives beyond the campus.",
    og: {
      image: "",
      title: "",
      description: "",
    },
  },
  data() {
    return {};
  },
  components: {
    Breadcrumb,
    PageHeader,
    Figure,
    QuickFact,
    Btn,
    Section,
  },
};
</script>

<style scoped lang="scss"></style>
